// A signed-in session: Cognito's tokens and when they expire, and who they're for, read from the ID token.
import type { AuthResult } from './cognito';

export interface Tokens {
  idToken: string;
  accessToken: string;
  refreshToken: string;
  /** When the access token expires, in milliseconds. */
  expiresAt: number;
}

export interface Who {
  sub: string;
  email: string;
  name: string | null;
}

/** A token's claims: its middle part, read. Never trusted for more than showing who's signed in. */
export function claims(jwt: string): Record<string, unknown> {
  const middle = jwt.split('.')[1] ?? '';
  try {
    const base64 = middle
      .replace(/-/g, '+')
      .replace(/_/g, '/')
      .padEnd(Math.ceil(middle.length / 4) * 4, '=');
    const bytes = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
    return JSON.parse(new TextDecoder().decode(bytes)) as Record<string, unknown>;
  } catch {
    return {};
  }
}

export function whoOf(tokens: Tokens): Who {
  const c = claims(tokens.idToken);
  return {
    sub: typeof c.sub === 'string' ? c.sub : '',
    email: typeof c.email === 'string' ? c.email : '',
    name: typeof c.name === 'string' && c.name.trim() ? c.name : null,
  };
}

/** Tokens from what Cognito answered, keeping the refresh token it didn't replace. */
export function tokensFrom(result: AuthResult, refreshToken: string | undefined, now: number): Tokens {
  const exp = claims(result.AccessToken).exp;
  return {
    idToken: result.IdToken,
    accessToken: result.AccessToken,
    refreshToken: result.RefreshToken ?? refreshToken ?? '',
    expiresAt: typeof exp === 'number' ? exp * 1000 : now + result.ExpiresIn * 1000,
  };
}

/** Whether something read from storage is a session. */
export function isTokens(value: unknown): value is Tokens {
  const t = value as Partial<Tokens> | null;
  return (
    typeof t?.idToken === 'string' &&
    typeof t.accessToken === 'string' &&
    typeof t.refreshToken === 'string' &&
    typeof t.expiresAt === 'number'
  );
}
